import { useState, DragEvent, useRef, ChangeEvent, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface FileDropZoneProps {
    onFilesAdd: (files: File[]) => void;
    acceptedExtensions?: string[];
    error?: boolean;
    helperText?: ReactNode;
    maxFiles?: number;
}

export default function FileDropZone({
    onFilesAdd,
    acceptedExtensions = [],
    error,
    helperText,
    maxFiles = Infinity,
}: FileDropZoneProps) {
    const [isDragActive, setIsDragActive] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        setValidationError(null);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            validateAndDrop(Array.from(e.dataTransfer.files));
        }
    };

    const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
        setValidationError(null);
        if (e.target.files && e.target.files.length > 0) {
            validateAndDrop(Array.from(e.target.files));
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const validateAndDrop = (newFiles: File[]) => {
        if (newFiles.length > maxFiles) {
            setValidationError(t('Too many files. Maximum {{maxFiles}} files allowed', { maxFiles }));
            return;
        }

        const validFiles: File[] = [];
        for (const file of newFiles) {
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (acceptedExtensions.length > 0 && !acceptedExtensions.includes(fileExtension)) {
                setValidationError(
                    t('File type not accepted. Accepted: {{acceptedExtensions}}', {
                        acceptedExtensions: acceptedExtensions.join(', '),
                    }),
                );
                return;
            }
            validFiles.push(file);
        }

        onFilesAdd(validFiles);
    };

    return (
        <Box
            onClick={() => inputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'border .24s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 150,
            }}
        >
            <CloudUpload sx={{ fontSize: 48, color: isDragActive ? 'primary.main' : 'text.secondary' }} />
            <Box>
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    {t('Drag & drop files here')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {acceptedExtensions.length > 0
                        ? t('Only {{acceptedExtensions}} files are accepted', {
                              acceptedExtensions: acceptedExtensions.join(', '),
                          })
                        : t('All file types are accepted')}
                </Typography>
                {(validationError || error) && (
                    <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                        {validationError || helperText}
                    </Typography>
                )}
            </Box>
            <input
                type="file"
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={handleFileInput}
                accept={acceptedExtensions.join(',')}
                multiple={maxFiles > 1}
            />
        </Box>
    );
}
